// Service pages for secondlifesoftware.com, rendered at /services/:slug by
// ServicePage.js. Each is an indexable, prerendered page with its own meta +
// Service JSON-LD. To add one: add an object here, add "/services/<slug>" to
// reactSnap.include in package.json, and it is auto-added to the sitemap via
// scripts/gen-sitemap.js (which reads src/data/siteRoutes.js).
//
// Bodies are Markdown rendered by the dependency-free Markdown component. Keep
// the tone straight-talk and founder-friendly — this is also the content LLMs
// quote when someone asks "who builds MVPs for non-technical founders."

const services = [
  {
    slug: 'mvp-development',
    name: 'MVP Development',
    title: 'MVP Development for Startups & Founders',
    metaDescription:
      'MVP development for non-technical founders. We build the smallest launchable version of your product — fast — so you can validate with real users. US-based. Book a free call.',
    tagline: 'Build the smallest version that proves your idea works.',
    intro:
      'We help founders turn an idea into a minimum viable product real users can actually use — scoped tightly, built well, and launched in weeks, not years.',
    body: `An MVP (minimum viable product) is the smallest version of your product that delivers real value and answers the one question that matters: *will people use this?* We build MVPs for non-technical founders who need a credible product in users' hands without spending a year or a fortune to get there.

## What we do

- **Scope it down.** The biggest cost in any build is scope. We help you find the one or two features that prove your idea and cut everything else — for now.
- **Design the core flow.** You'll click through your product before we write production code, the cheapest moment to change your mind.
- **Build in two-week cycles.** You see working software every sprint, not a status report at the end.
- **Launch for real.** Live infrastructure, payments, email, and — if you need it — the app stores.

## What a typical MVP includes

User accounts, one core workflow done well, a clean interface built on a proven component library, a database, payments (Stripe), and the integrations your product actually needs. Most founder MVPs land in the **$15,000–$40,000** range; a single-workflow validation build can start lower. We hold a $5,000 project minimum.

## Why founders pick us

You work directly with the senior people building your product — no account managers, no junior bait-and-switch. We own the "translate a non-technical vision into a technical plan" part so you don't have to learn to code. And you keep everything: code, domain, and cloud accounts, in writing.

## Who this is for

First-time founders validating a new idea, domain experts who see a problem worth solving, and small businesses that need custom software but don't have a dev team. If you already have a stalled build, see [MVP Rescue](/services/mvp-rescue).`,
    faq: [
      {
        q: 'How long does it take to build an MVP?',
        a: 'Most MVPs go from first conversation to launch in 2–4 months. A very lean, single-workflow product can ship in 4–6 weeks.',
      },
      {
        q: 'How much does an MVP cost?',
        a: 'Most founder MVPs land in the $15,000–$40,000 range. We hold a $5,000 project minimum; below that a no-code tool is usually a better fit.',
      },
      {
        q: 'Do I need a technical background?',
        a: 'No. Working with non-technical founders is a core part of what we do — we translate your vision into a technical plan and handle the build.',
      },
    ],
  },
  {
    slug: 'custom-software-development',
    name: 'Custom Software Development',
    title: 'Custom Software Development',
    metaDescription:
      'Custom software development for businesses and startups — bespoke web apps, internal tools, and automation built to your exact workflow. US-based studio. Book a free call.',
    tagline: 'Software built around your workflow, not a template.',
    intro:
      'When off-the-shelf tools do not fit how you actually work, we build custom software that does — from internal tools to full customer-facing platforms.',
    body: `Off-the-shelf software forces your business to bend around it. Custom software bends around your business. We build bespoke applications — internal tools, dashboards, automation, and customer-facing platforms — for companies whose workflow is their edge.

## What we build

- **Internal tools & dashboards** that replace spreadsheets and manual work.
- **Customer-facing web applications** with accounts, billing, and real workflows.
- **Automation** that removes repetitive work — data pipelines, scrapers, scheduled jobs, notifications.
- **Integrations** that connect the tools you already use (CRMs, payment systems, email/SMS, third-party APIs).

## How we work

We start with your actual process — what people do today, where it breaks, and what "done" looks like. Then we build in two-week cycles so you see and use working software early and steer as we go. You own all the code and accounts.

## Why custom (and when not to)

Custom software is worth it when the workflow is core to your business, when existing tools create real friction or cost, or when integration and data ownership matter. If a proven SaaS tool already does the job, we'll tell you — the cheapest software is the one you don't have to build.

## Built to last

We build on modern, well-supported foundations, document what we ship, and hand over cleanly. The goal is software you can maintain and grow — not a black box that traps you.`,
    faq: [
      {
        q: 'How is custom software priced?',
        a: 'By scope. Small internal tools can start in the low five figures; larger platforms scale from there. We give a clear written scope and range before any work begins.',
      },
      {
        q: 'Will we own the code?',
        a: 'Yes — always, in writing. You hold the repository, domain, and cloud accounts.',
      },
    ],
  },
  {
    slug: 'ai-app-development',
    name: 'AI App Development',
    title: 'AI App Development & AI-Powered Tools',
    metaDescription:
      'AI app development — LLM-powered apps, internal tools, chatbots, and automation built on modern models. Practical AI that ships, for founders and businesses. Book a free call.',
    tagline: 'Practical AI that ships — not a science project.',
    intro:
      'We build AI-powered products and internal tools on modern large language models — the useful kind that solve a real problem, not a demo that never leaves the lab.',
    body: `Everyone wants AI in their product. The hard part is building something genuinely useful and reliable instead of an impressive demo that falls apart in production. We build practical, AI-powered applications and tools on modern large language models (LLMs) — designed around a real workflow and shipped to real users.

## What we build

- **AI-powered apps** — products where an LLM does real work: drafting, summarizing, extracting, classifying, answering.
- **Internal AI tools** that automate the repetitive knowledge work slowing your team down.
- **Chatbots & assistants** grounded in your own content and data (retrieval-augmented generation).
- **AI automation** — pipelines that read messy inputs and produce structured, usable output.

## We build it right

Practical AI is more than an API call. We handle the parts that make it reliable: grounding answers in your data, guarding against hallucination, sensible fallbacks when the model is wrong, cost control, and evaluation so you know it actually works. We pick the model that fits the job rather than chasing hype.

## Where AI helps (and where it doesn't)

AI shines at language-shaped work: summarizing, extracting, classifying, drafting, and conversing over natural language. It's the wrong tool for tasks that need exactness and auditability. Part of our job is telling you which is which — so you spend on AI where it pays off and skip it where it doesn't.

## Who this is for

Founders building an AI-first product, and businesses that want to fold AI into an existing product or internal process without betting the company on a black box.`,
    faq: [
      {
        q: 'Which AI models do you use?',
        a: 'We choose the model per task — the latest Claude, GPT, and open models all have strengths. We optimize for reliability and cost, not hype.',
      },
      {
        q: 'How much does an AI app cost to build and run?',
        a: 'Build cost depends on scope like any software; running cost adds per-use model API fees. We design for cost control and give you a realistic estimate of both.',
      },
    ],
  },
  {
    slug: 'web-app-development',
    name: 'Web Application Development',
    title: 'Web Application Development',
    metaDescription:
      'Web application development from front end to infrastructure — fast, reliable, scalable web apps for startups and businesses. US-based studio. Book a free call.',
    tagline: 'Fast, reliable web apps — front end to infrastructure.',
    intro:
      'We design and build modern web applications end to end: the interface users see, the systems behind it, and the infrastructure that keeps it running.',
    body: `A web application is software your users reach in a browser — no install, works everywhere. We build them end to end: the front-end interface, the back-end systems and database, and the infrastructure that keeps it fast and reliable as you grow.

## Full-stack, one team

- **Front end** — clean, responsive interfaces that work on any device.
- **Back end** — the logic, database, and APIs that power the app.
- **Infrastructure** — hosting, deployment, security, and monitoring set up right from day one.

Because one senior team owns all three, there's no finger-pointing between designers, developers, and "the server people" when something needs to change.

## Built to scale sensibly

We build for your first hundred users, not an imaginary million — but on foundations that scale cleanly when the users show up. Proven building blocks (authentication, payments, component libraries) instead of reinventing wheels, so budget goes toward what makes your product different.

## Reliable after launch

Launch is the start. We set up monitoring and error tracking so problems surface before your users report them, and we hand over clean, documented code and accounts you fully own.

## Who this is for

Founders and businesses that need a real, browser-based product — SaaS tools, marketplaces, dashboards, booking systems, internal platforms — built well and maintained.`,
    faq: [
      {
        q: 'What is the difference between a web app and a website?',
        a: 'A website mostly presents information; a web app lets users do things — log in, enter data, transact. We build the interactive, app side.',
      },
      {
        q: 'Do you build mobile apps too?',
        a: 'Many products are best as responsive web apps that work on phones. When you genuinely need native iOS/Android, we scope that with you.',
      },
    ],
  },
  {
    slug: 'mvp-rescue',
    name: 'MVP Rescue',
    title: 'MVP Rescue — Fix a Stalled or Inherited Codebase',
    metaDescription:
      'Inherited a messy or stalled codebase? MVP Rescue audits, stabilizes, and finishes builds left by a previous developer or agency. US-based studio. Book a free call.',
    tagline: 'Stalled build? We audit, stabilize, and finish it.',
    intro:
      'When a previous developer or agency left you with a half-built or broken product, we step in, figure out what you actually have, and get it to the finish line.',
    body: `It's one of the most common calls we get: a founder paid to have a product built, and now it's stalled, buggy, or abandoned — the developer went quiet, the agency moved on, and no one can say what actually works. MVP Rescue is for exactly this.

## Step one: an honest audit

Before promising anything, we assess what you have — the code, the accounts, the infrastructure — and give you a straight answer: what's salvageable, what isn't, and the fastest realistic path to a working product. Sometimes finishing the existing build is right; sometimes rebuilding the core is genuinely cheaper. We'll tell you which, even when it's not the answer you hoped for.

## Then we stabilize and finish

- **Take ownership** of the code, domain, and cloud accounts (and help you recover them if the last team still holds the keys — the most expensive trap of all).
- **Stop the bleeding** — fix what's broken and get it running reliably.
- **Finish the MVP** — build the remaining core features and get it launched.

## Why founders trust us with this

Picking up someone else's code well is a specific skill — reading unfamiliar systems, spotting landmines, and making a calm plan instead of a rewrite for its own sake. We do this regularly: audit existing code, improve what's there, and move it forward without drama.

## Who this is for

Founders with a half-finished product, a build that broke after the developer left, or an inherited codebase nobody understands. Starting fresh instead? See [MVP Development](/services/mvp-development).`,
    faq: [
      {
        q: 'Can you take over a project another developer started?',
        a: 'Yes — that\'s exactly what MVP Rescue is. We regularly pick up projects mid-stream, audit the code, and move them to launch.',
      },
      {
        q: 'What if my old developer still controls the accounts?',
        a: 'We help you recover ownership of your code, domain, and cloud accounts as part of the rescue. Owning your own keys is non-negotiable.',
      },
      {
        q: 'Should I fix the existing build or start over?',
        a: 'That\'s the first thing our audit answers. Sometimes finishing is fastest; sometimes rebuilding the core is cheaper. We give you an honest recommendation.',
      },
    ],
  },
];

export default services;

export function getServiceBySlug(slug) {
  return services.find((s) => s.slug === slug);
}
